import React, { useState, useEffect, useRef } from "react";
import { database, auth, firestore } from "../firebaseConfig";
import { ref, update, onValue, push, set, remove, get } from "firebase/database";
import { collection, query, where, getDocs, writeBatch, doc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import WorldSettingsTopics from "./WorldSettingsTopics";
import WhatsappSettings from "./WhatsappSettings";
import WhisperSettings from "./WhisperSettings";
import AgentsSettings from "./AgentsSettings";
import AITagsSettings from "./AITagsSettings";
import MoreWorldSettings from "./MoreWorldSettings";
import "./WorldSettings.css";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { generateEmailFooter } from './EmailFooterGenerator';
import downArrow from '../img/down.svg';
import { encryptEmailPassword, isPasswordEncrypted } from '../utils/encryption';
import { CLOUD_FUNCTION_URLS } from '../utils/cloudFunctions';

const WorldSettings = () => {
  const [activeTab, setActiveTab] = useState('email');
  const [userId, setUserId] = useState(null);
  const [emailSettings, setEmailSettings] = useState({
    imap_host: "imap.hostinger.com",
    imap_port: "993",
    email_username: "",
    email_password: "",
    encryption_type: "SSL",
    subject_line: "",
    smtp_host: "smtp.hostinger.com",
    smtp_port: "465",
    smtp_encryption: "true",
    footer_text: "",
    footer_image_url: "",
    email_queue_duration: "10",
    footer_font: "Helvetica Neue",
    footer_image_position: "below",
    footer_image_size: 80,
    user_name: "BonusClub"
  });
  const [aiSettings, setAiSettings] = useState({
    promptTemplate: `You are \${actualSenderName}, writing casual but professional emails to potential business partners. 
You work at BonusClub, a company helping hosts boost their online visibility through authentic guest experiences and reviews.

Write in a natural, conversational style as if you're sending a quick email to a colleague. 
Keep it human, short, friendly, and to the point - like a real person would write.

CRITICAL RULES (MUST FOLLOW):
- Write in first person AS \${actualSenderName} (use "I" and write from \${actualSenderName}'s perspective)
- Address the recipient by their first name (\${firstName || 'Dear'})
- NEVER include any kind of signature, closing phrases, other than "Best, \${actualSenderName}".
- Just end with your last signature - no goodbyes, no sign-offs
- Keep it brief and genuine
- DO NOT include any footer, signature, or closing line
- IMPORTANT: Write the entire email in \${language || 'English'} language`,
    model: "gpt-4o-mini",
    temperature: 0.9,
    max_tokens: 250,
    language: "English",
    strictInstructions: `- Start with a proper introduction that builds rapport (especially for first contact)
- If this is the first email ("Introduce Yourself"), make sure to properly introduce yourself and your company
- Include a brief personal touch or connection to build rapport (e.g., compliment their business, mention something specific you noticed)
- Address them personally (use their first name if available)
- Write like a real person sending a quick email
- Keep it short and sweet (max 4-5 sentences)
- NO greetings or signatures at the end
- End naturally with your last content point - DO NOT add any closing phrase
- Avoid any marketing-speak or formal language
- NEVER add your name, "thanks", "cheers", "regards" or ANY closing phrase at the end
- The email should end abruptly with your last content sentence`,
    cleanupGuidelines: `- Remove any redundant or repetitive phrases
- Ensure the text flows naturally and conversationally
- Fix any grammatical errors while maintaining a casual tone
- Remove any AI-like phrasing or overly formal language
- Make sure the email sounds like it was written by a real person
- Eliminate unnecessary pleasantries and corporate jargon
- Keep sentences short and direct
- Remove any closing phrases or signatures that might have been added`,
    replyAgentGuidelines: `- Analyze the incoming email's tone and enthusiasm level
- Match the sender's communication style and formality level
- Reference specific points mentioned in the original email
- Keep responses concise and focused on the main topic
- Show genuine interest in their business or inquiry
- Provide helpful information without being overly promotional
- Use natural language that sounds conversational
- Address any questions or concerns directly
- Maintain professional boundaries while being friendly
- End with a clear next step or invitation for further discussion`,
    replyAgentTemperature: 0.7
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Neuer State für den HTML-Editor
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const editorRef = useRef(null);

  // Neue State-Variablen für die Danger Zone
  const [imports, setImports] = useState([]);
  const [selectedImport, setSelectedImport] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [importCounts, setImportCounts] = useState({});
  
  // State für ausklappbare Info-Bereiche
  const [expandedSections, setExpandedSections] = useState({});
  
  // State für Smart Inbox Toggle
  const [inboxActive, setInboxActive] = useState(false);

  // Funktion zum Umschalten des Ausklappstatus eines Containers
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Auth-Überwachung: Hol User-ID
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Event-Listener für das Öffnen bestimmter Tabs
  useEffect(() => {
    // Event-Handler-Funktion
    const handleOpenSettingsTab = (event) => {
      if (event.detail && event.detail.tab) {
        setActiveTab(event.detail.tab);
      }
    };

    // Event-Listener hinzufügen
    document.addEventListener('openSettingsTab', handleOpenSettingsTab);

    // Überprüfen, ob das Tab in der Session gespeichert ist
    const savedTab = sessionStorage.getItem('openSettingsTab');
    if (savedTab) {
      setActiveTab(savedTab);
      sessionStorage.removeItem('openSettingsTab');
    }

    // Cleanup-Funktion
    return () => {
      document.removeEventListener('openSettingsTab', handleOpenSettingsTab);
    };
  }, []);

  // Lade vorhandene Einstellungen aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    // Email-Einstellungen laden
    const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
    const unsubscribeEmailListener = onValue(
      emailSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setEmailSettings(snapshot.val());
        }
        
        // Jetzt auch die Footer-Einstellungen laden
        const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
        onValue(footerSettingsRef, (snapshot) => {
          if (snapshot.exists()) {
            // Kombiniere die Footer-Einstellungen mit den E-Mail-Einstellungen
            setEmailSettings(prev => ({
              ...prev,
              footer_text: snapshot.val().footer_text || "",
              footer_image_url: snapshot.val().footer_image_url || "",
              footer_image_position: snapshot.val().footer_image_position || "below",
              footer_font: snapshot.val().footer_font || "Helvetica Neue",
              footer_image_size: snapshot.val().footer_image_size || 80
            }));
          }
          setLoading(false);
        });
      },
      (error) => {
        console.error("Fehler beim Laden der Email-Einstellungen", error);
        setLoading(false);
      }
    );
    
    // AI-Einstellungen laden
    const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
    const unsubscribeAiListener = onValue(
      aiSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setAiSettings(snapshot.val());
        } else {
          // Wenn keine AI-Einstellungen existieren, setze die Standardwerte
          setAiSettings({
            promptTemplate: `You are \${actualSenderName}, writing casual but professional emails to potential business partners. 

Write in a natural, conversational style as if you're sending a quick email to a colleague. 
Keep it human, short, friendly, and to the point - like a real person would write.

CRITICAL RULES (MUST FOLLOW):
- Write in first person AS \${actualSenderName} (use "I" and write from \${actualSenderName}'s perspective)
- Address the recipient by their first name (\${firstName || 'Dear'})
- NEVER include any kind of signature, closing phrases, other than "Best, \${actualSenderName}".
- Just end with your last signature - no goodbyes, no sign-offs
- Keep it brief and genuine
- DO NOT include any footer, signature, or closing line
- IMPORTANT: Write the entire email in \${language || 'English'} language`,
            model: "gpt-4o-mini",
            temperature: 0.9,
            max_tokens: 250,
            language: "English",
            strictInstructions: `- Start with a proper introduction that builds rapport (especially for first contact)
- If this is the first email ("Introduce Yourself"), make sure to properly introduce yourself and your company
- Include a brief personal touch or connection to build rapport (e.g., compliment their business, mention something specific you noticed)
- Address them personally (use their first name if available)
- Write like a real person sending a quick email
- Keep it short and sweet (max 4-5 sentences)
- NO greetings or signatures at the end
- End naturally with your last content point - DO NOT add any closing phrase
- Avoid any marketing-speak or formal language
- NEVER add your name, "thanks", "cheers", "regards" or ANY closing phrase at the end
- The email should end abruptly with your last content sentence`,
            cleanupGuidelines: `- Remove any redundant or repetitive phrases
- Ensure the text flows naturally and conversationally
- Fix any grammatical errors while maintaining a casual tone
- Remove any AI-like phrasing or overly formal language
- Make sure the email sounds like it was written by a real person
- Eliminate unnecessary pleasantries and corporate jargon
- Keep sentences short and direct
- Remove any closing phrases or signatures that might have been added`,
            replyAgentGuidelines: `- Analyze the incoming email's tone and enthusiasm level
- Match the sender's communication style and formality level
- Reference specific points mentioned in the original email
- Keep responses concise and focused on the main topic
- Show genuine interest in their business or inquiry
- Provide helpful information without being overly promotional
- Use natural language that sounds conversational
- Address any questions or concerns directly
- Maintain professional boundaries while being friendly
- End with a clear next step or invitation for further discussion`,
            replyAgentTemperature: 0.7
          });
        }
      },
      (error) => {
        console.error("Fehler beim Laden der AI-Einstellungen", error);
      }
    );
    
    // Auto-Process Emails Einstellung laden
    const autoProcessRef = ref(database, `/World/${userId}/settings/autoProcessEmails`);
    const unsubscribeAutoProcessListener = onValue(
      autoProcessRef,
      (snapshot) => {
        const autoProcessValue = snapshot.exists() ? snapshot.val() : false;
        setAiSettings(prev => ({
          ...prev,
          autoProcessEmails: autoProcessValue
        }));
      },
      (error) => {
        console.error("Fehler beim Laden der autoProcessEmails-Einstellung", error);
      }
    );
    
    // Smart Inbox Active Einstellung laden
    const inboxActiveRef = ref(database, `/World/${userId}/settings/inboxActive`);
    const unsubscribeInboxActiveListener = onValue(
      inboxActiveRef,
      (snapshot) => {
        const inboxActiveValue = snapshot.exists() ? snapshot.val() : false;
        setInboxActive(inboxActiveValue);
      },
      (error) => {
        console.error("Fehler beim Laden der inboxActive-Einstellung", error);
      }
    );
    
    return () => {
      unsubscribeEmailListener();
      unsubscribeAiListener();
      unsubscribeAutoProcessListener();
      unsubscribeInboxActiveListener();
    };
  }, [userId]);

  // Lade verfügbare Imports aus der Datenbank (Firestore)
  useEffect(() => {
    if (!userId) return;

    const fetchImportsAndCounts = async () => {
      try {
        // 1. Hole die Import-Namen aus der `import_names` Sammlung
        const importsCollectionRef = collection(firestore, `World/${userId}/import_names`);
        const importsSnapshot = await getDocs(importsCollectionRef);
        const importsList = importsSnapshot.docs.map(doc => doc.id).sort();
        setImports(importsList);

        // 2. Hole die Anzahl der Einträge für jeden Import
        const dbCollectionRef = collection(firestore, `World/${userId}/db`);
        const dbSnapshot = await getDocs(dbCollectionRef);
        const counts = {};

        // Initialisiere die Zähler für alle Imports mit 0
        importsList.forEach(name => {
          counts[name] = 0;
        });

        // Zähle die Einträge aus der `db` Sammlung
        dbSnapshot.forEach(doc => {
          const entry = doc.data();
          if (entry.import_name && counts.hasOwnProperty(entry.import_name)) {
            counts[entry.import_name]++;
          }
        });

        setImportCounts(counts);
      } catch (error) {
        console.error("Fehler beim Laden der Imports aus Firestore:", error);
        setImports([]);
        setImportCounts({});
      }
    };

    fetchImportsAndCounts();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmailSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAiChange = (e) => {
    const { name, value } = e.target;
    setAiSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("Kein Benutzer angemeldet.");
      return;
    }
    
    try {
      // Setze den Button-Status auf "gespeichert"
      setIsSaved(true);
      
      // Speichere die entsprechenden Einstellungen je nach aktivem Tab
      if (activeTab === 'ai') {
        const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
        await update(aiSettingsRef, aiSettings);
        
        // Speichere auch die autoProcessEmails Einstellung separat
        const autoProcessRef = ref(database, `/World/${userId}/settings/autoProcessEmails`);
        await set(autoProcessRef, aiSettings.autoProcessEmails || false);
        
        // Speichere auch die inboxActive Einstellung separat
        const inboxActiveRef = ref(database, `/World/${userId}/settings/inboxActive`);
        await set(inboxActiveRef, inboxActive);
        
        console.log("AI-Einstellungen erfolgreich gespeichert:", aiSettings);
        console.log("InboxActive erfolgreich gespeichert:", inboxActive);
        setMessage("AI settings saved successfully.");
      } else if (activeTab === 'emailPreferences') {
        // Logge die zu speichernden Einstellungen
        console.log("Speichere E-Mail-Einstellungen:", emailSettings);
        
        // Verschlüssele das Passwort, falls vorhanden und noch nicht verschlüsselt
        let settingsToSave = { ...emailSettings };
        if (emailSettings.email_password && !isPasswordEncrypted(emailSettings.email_password)) {
          console.log('🔐 Encrypting email password before saving...');
          settingsToSave.email_password = await encryptEmailPassword(emailSettings.email_password);
          console.log('✅ Email password encrypted successfully');
        } else if (emailSettings.email_password) {
          console.log('ℹ️ Email password is already encrypted');
        }
        
        // Speichere allgemeine E-Mail-Einstellungen
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        await update(emailSettingsRef, settingsToSave);
        
        // Speichere Footer-Einstellungen separat unter dem richtigen Pfad
        const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
        const footerSettings = {
          footer_text: emailSettings.footer_text || "",
          footer_image_url: emailSettings.footer_image_url || "",
          footer_image_position: emailSettings.footer_image_position || "below",
          footer_font: emailSettings.footer_font || "Helvetica Neue",
          footer_image_size: emailSettings.footer_image_size || 80
        };
        
        await update(footerSettingsRef, footerSettings);
        
        console.log("E-Mail-Einstellungen erfolgreich gespeichert:", settingsToSave);
        console.log("Footer-Einstellungen erfolgreich gespeichert:", footerSettings);
        setMessage("Email settings saved successfully.");
      } else {
        // Logge die zu speichernden Einstellungen
        console.log("Save email settings:", emailSettings);
        
        // Verschlüssele das Passwort, falls vorhanden und noch nicht verschlüsselt
        let settingsToSave = { ...emailSettings };
        if (emailSettings.email_password && !isPasswordEncrypted(emailSettings.email_password)) {
          console.log('🔐 Encrypting email password before saving...');
          settingsToSave.email_password = await encryptEmailPassword(emailSettings.email_password);
          console.log('✅ Email password encrypted successfully');
        } else if (emailSettings.email_password) {
          console.log('ℹ️ Email password is already encrypted');
        }
        
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        await update(emailSettingsRef, settingsToSave);
        
        console.log("Allgemeine Einstellungen erfolgreich gespeichert:", settingsToSave);
        setMessage("Settings saved successfully.");
      }

      // Setze den Button und die Message nach 3 Sekunden zurück
      setTimeout(() => {
        setIsSaved(false);
        setMessage("");
      }, 3000);
    } catch (error) {
      console.error("Fehler beim Speichern der Einstellungen:", error);
      setMessage("Error saving settings: " + error.message);
      // Bei einem Fehler auch nach 3 Sekunden die Fehlermeldung ausblenden
      setTimeout(() => {
        setMessage("");
      }, 3000);
      setIsSaved(false);
    }
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = e.dataTransfer?.files || [];
    if (droppedFiles.length > 0) {
      await handleImageUpload({ target: { files: [droppedFiles[0]] } });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Überprüfe Dateityp
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setMessage("Only JPG, PNG, GIF and SVG files are allowed.");
      return;
    }
    
    // Überprüfe Dateigröße (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage("The file is too large. Maximum size: 2MB");
      return;
    }
    
    try {
      setUploading(true);
      setMessage("Picture is being uploaded...");
      
      // Erstelle einen eindeutigen Dateinamen
      const fileName = `footer_images/${userId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      console.log("Speichere Bild unter:", fileName);
      
      const storage = getStorage();
      const fileRef = storageRef(storage, fileName);
      
      // Lade die Datei hoch
      await uploadBytes(fileRef, file);
      console.log("Picture uploaded successfully");
      
      // Hole die Download-URL
      const downloadURL = await getDownloadURL(fileRef);
      console.log("Picture URL:", downloadURL);
      
      // Aktualisiere die Einstellungen
      setEmailSettings(prev => {
        const updated = {
          ...prev,
          footer_image_url: downloadURL
        };
        console.log("Updated email settings:", updated);
        return updated;
      });
      
      setMessage("Picture uploaded successfully! Click 'Save Settings' to save the changes.");
    } catch (error) {
      console.error("Error uploading image:", error);
      setMessage("Error uploading image: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Funktion zum Formatieren des ausgewählten Textes im Editor
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    
    // Aktualisiere den Footer-Text nach der Formatierung
    if (editorRef.current) {
      setEmailSettings(prev => ({
        ...prev,
        footer_text: editorRef.current.innerHTML
      }));
    }
  };

  const EmailAccountManager = ({ userId }) => {
    const [emailAccounts, setEmailAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [showAddAccount, setShowAddAccount] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [selectedAccountLogs, setSelectedAccountLogs] = useState(null);
    const [emailLogs, setEmailLogs] = useState({});
    const [newAccount, setNewAccount] = useState({
      email: '',
      password: '',
      imap_host: 'imap.hostinger.com',
      imap_port: '993',
      encryption_type: 'SSL',
      smtp_host: 'smtp.hostinger.com',
      smtp_port: '465',
      smtp_encryption: 'true',
      sender_name: 'BonusClub',
      queue_duration: '10',
      sleep_time_start: '22:00',
      sleep_time_end: '08:00'
    });
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');

    // Lade alle E-Mail-Konten
    useEffect(() => {
      if (!userId) return;
      
      const emailAccountsRef = ref(database, `/World/${userId}/settings/senderEmails`);
      const unsubscribe = onValue(emailAccountsRef, (snapshot) => {
        if (snapshot.exists()) {
          const accounts = [];
          snapshot.forEach((childSnapshot) => {
            accounts.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setEmailAccounts(accounts);
          
          // Lade auch die Logs für alle Accounts
          loadEmailLogsForAccounts(accounts);
        } else {
          // Wenn keine Konten existieren, zeige eine Meldung an
          setEmailAccounts([]);
        }
        setIsLoading(false);
      });
      
      return () => unsubscribe();
    }, [userId]);

    // Funktion zum Laden der E-Mail-Logs für alle Accounts
    const loadEmailLogsForAccounts = async (accounts) => {
      if (!userId || !accounts.length) return;
      
      const logsData = {};
      
      for (const account of accounts) {
        try {
          const sanitizedEmail = account.email.replace(/[.#$\/\[\]]/g, '_');
          const logsRef = ref(database, `/World/${userId}/emailLogs/${sanitizedEmail}`);
          const snapshot = await get(logsRef);
          
          if (snapshot.exists()) {
            const logs = [];
            snapshot.forEach((childSnapshot) => {
              logs.push({
                id: childSnapshot.key,
                ...childSnapshot.val()
              });
            });
            
            // Sortiere nach Timestamp (neueste zuerst)
            logs.sort((a, b) => b.timestamp - a.timestamp);
            logsData[account.email] = logs;
          } else {
            logsData[account.email] = [];
          }
        } catch (error) {
          console.error(`Fehler beim Laden der Logs für ${account.email}:`, error);
          logsData[account.email] = [];
        }
      }
      
      setEmailLogs(logsData);
    };

    // Funktion zum Ermitteln des Log-Status für einen Account
    const getAccountLogStatus = (email) => {
      const logs = emailLogs[email] || [];
      if (logs.length === 0) return 'unknown';
      
      const latestLog = logs[0]; // Neuester Log (bereits sortiert)
      
      if (latestLog.status === 'success') {
        return 'success';
      } else if (latestLog.status === 'error') {
        return 'error';
      } else {
        return 'info';
      }
    };

    // Funktion zum Öffnen des Log-Modals
    const handleShowLogs = (account) => {
      setSelectedAccountLogs(account);
      setShowLogsModal(true);
    };

    // Neue Funktion zum Speichern der Verzögerungsdauer in den E-Mail-Einstellungen
    const updateQueueDurationInEmailSettings = async (email, queueDuration) => {
      try {
        // Sanitize die E-Mail-Adresse für die Verwendung als Schlüssel
        const sanitizedEmail = email.replace(/[.#$\/\[\]]/g, '_');
        const durationKey = `email_queue_duration_${sanitizedEmail}`;
        
        // Referenz zu den E-Mail-Einstellungen
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        
        // Hole die aktuellen Einstellungen
        const snapshot = await get(emailSettingsRef);
        const currentSettings = snapshot.exists() ? snapshot.val() : {};
        
        // Setze die Verzögerungsdauer für diesen Absender
        const updatedSettings = {
          ...currentSettings,
          [durationKey]: queueDuration
        };
        
        // Aktualisiere die Einstellungen
        await set(emailSettingsRef, updatedSettings);
        
        console.log(`Verzögerungsdauer für ${email} auf ${queueDuration} Minuten aktualisiert`);
      } catch (error) {
        console.error('Fehler beim Speichern der Verzögerungsdauer:', error);
        setMessage(`Fehler beim Speichern der Verzögerungsdauer: ${error.message}`);
      }
    };

    // Funktion zum Hinzufügen eines neuen E-Mail-Kontos
    const handleAddAccount = async () => {
      if (!newAccount.email || !newAccount.password) {
        setMessage('Bitte gib E-Mail-Adresse und Passwort ein.');
        return;
      }
      
      try {
        setMessage('Speichere E-Mail-Konto...');
        
        // Verschlüssele das Passwort, falls es noch nicht verschlüsselt ist
        let passwordToSave = newAccount.password;
        if (!isPasswordEncrypted(newAccount.password)) {
          console.log('🔐 Encrypting password before saving...');
          passwordToSave = await encryptEmailPassword(newAccount.password);
          console.log('✅ Password encrypted successfully');
        } else {
          console.log('ℹ️ Password is already encrypted');
        }
        
        // Stelle sicher, dass alle erforderlichen Felder vorhanden sind und korrekt formatiert sind
        const accountToSave = {
          email: newAccount.email.trim(),
          password: passwordToSave,  // Verwende das verschlüsselte Passwort
          imap_host: newAccount.imap_host || 'imap.hostinger.com',
          imap_port: newAccount.imap_port || '993',
          encryption_type: newAccount.encryption_type || 'SSL',
          smtp_host: newAccount.smtp_host || 'smtp.hostinger.com',
          smtp_port: newAccount.smtp_port || '465',
          // Stelle sicher, dass smtp_encryption als String gespeichert wird
          smtp_encryption: String(newAccount.smtp_encryption || 'true'),
          sender_name: newAccount.sender_name || 'BonusClub',
          queue_duration: newAccount.queue_duration || '10',
          sleep_time_start: newAccount.sleep_time_start || '22:00',
          sleep_time_end: newAccount.sleep_time_end || '08:00',
          encrypted: true  // Markiere als verschlüsselt
        };
        
        // Speichere das Konto
        const emailAccountsRef = ref(database, `/World/${userId}/settings/senderEmails`);
        const newAccountRef = push(emailAccountsRef);
        
        await set(newAccountRef, accountToSave);
        
        // Speichere auch die Verzögerungsdauer in den E-Mail-Einstellungen
        await updateQueueDurationInEmailSettings(accountToSave.email, accountToSave.queue_duration);
        
        setMessage('E-Mail-Konto erfolgreich hinzugefügt. Bitte beachte, dass die Verbindung erst beim Senden einer E-Mail getestet wird.');
        setShowAddAccount(false);
        setNewAccount({
          email: '',
          password: '',
          imap_host: 'imap.hostinger.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.hostinger.com',
          smtp_port: '465',
          smtp_encryption: 'true',
          sender_name: 'BonusClub',
          queue_duration: '10',
          sleep_time_start: '22:00',
          sleep_time_end: '08:00'
        });
      } catch (error) {
        console.error('Fehler beim Hinzufügen des E-Mail-Kontos:', error);
        setMessage(`Fehler: ${error.message}`);
      }
    };

    // Funktion zum Aktualisieren eines bestehenden E-Mail-Kontos
    const handleUpdateAccount = async () => {
      if (!selectedAccount) return;
      
      try {
        setMessage('Aktualisiere E-Mail-Konto...');
        
        // Entferne die ID aus dem Objekt, bevor es gespeichert wird
        const { id, ...accountData } = selectedAccount;
        
        // Verschlüssele das Passwort, falls es noch nicht verschlüsselt ist
        let passwordToSave = accountData.password;
        if (accountData.password && !isPasswordEncrypted(accountData.password)) {
          console.log('🔐 Encrypting password before updating...');
          passwordToSave = await encryptEmailPassword(accountData.password);
          console.log('✅ Password encrypted successfully');
        } else if (accountData.password) {
          console.log('ℹ️ Password is already encrypted');
        }
        
        // Stelle sicher, dass alle erforderlichen Felder vorhanden sind und korrekt formatiert sind
        const accountToUpdate = {
          email: accountData.email.trim(),
          password: passwordToSave,  // Verwende das verschlüsselte Passwort
          imap_host: accountData.imap_host || 'imap.hostinger.com',
          imap_port: accountData.imap_port || '993',
          encryption_type: accountData.encryption_type || 'SSL',
          smtp_host: accountData.smtp_host || 'smtp.hostinger.com',
          smtp_port: accountData.smtp_port || '465',
          // Stelle sicher, dass smtp_encryption als String gespeichert wird
          smtp_encryption: String(accountData.smtp_encryption || 'true'),
          sender_name: accountData.sender_name || 'BonusClub',
          queue_duration: accountData.queue_duration || '10',
          sleep_time_start: accountData.sleep_time_start || '22:00',
          sleep_time_end: accountData.sleep_time_end || '08:00',
          encrypted: true  // Markiere als verschlüsselt
        };
        
        const accountRef = ref(database, `/World/${userId}/settings/senderEmails/${selectedAccount.id}`);
        
        await update(accountRef, accountToUpdate);
        
        // Aktualisiere auch die Verzögerungsdauer in den E-Mail-Einstellungen
        await updateQueueDurationInEmailSettings(accountToUpdate.email, accountToUpdate.queue_duration);
        
        setMessage('E-Mail-Account updated successfully.');
      } catch (error) {
        console.error('Error updating E-Mail-Account:', error);
        setMessage(`Error: ${error.message}`);
      }
    };

    // Funktion zum Entfernen der Verzögerungsdauer aus den E-Mail-Einstellungen
    const removeQueueDurationFromEmailSettings = async (email) => {
      try {
        // Sanitize die E-Mail-Adresse für die Verwendung als Schlüssel
        const sanitizedEmail = email.replace(/[.#$\/\[\]]/g, '_');
        const durationKey = `email_queue_duration_${sanitizedEmail}`;
        
        // Referenz zu den E-Mail-Einstellungen
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        
        // Hole die aktuellen Einstellungen
        const snapshot = await get(emailSettingsRef);
        if (!snapshot.exists()) return;
        
        const currentSettings = snapshot.val();
        
        // Prüfe, ob die Einstellung existiert
        if (!(durationKey in currentSettings)) return;
        
        // Entferne die Verzögerungsdauer für diesen Absender
        const { [durationKey]: _, ...updatedSettings } = currentSettings;
        
        // Aktualisiere die Einstellungen
        await set(emailSettingsRef, updatedSettings);
        
        console.log(`Verzögerungsdauer für ${email} wurde entfernt`);
      } catch (error) {
        console.error('Fehler beim Entfernen der Verzögerungsdauer:', error);
      }
    };

    // Funktion zum Löschen eines E-Mail-Kontos
    const handleDeleteAccount = async (account) => {
      if (!account || !window.confirm(`Are you sure you want to delete the email account ${account.email}?`)) {
        return;
      }
      
      try {
        setMessage('Deleting email account...');
        
        // Entferne die Prüfung auf 'default' ID
        const accountRef = ref(database, `/World/${userId}/settings/senderEmails/${account.id}`);
        
        await remove(accountRef);
        
        // Entferne auch die Verzögerungsdauer aus den E-Mail-Einstellungen
        await removeQueueDurationFromEmailSettings(account.email);
        
        // Wenn das gelöschte Konto das ausgewählte war, setze selectedAccount zurück
        if (selectedAccount && selectedAccount.id === account.id) {
          setSelectedAccount(null);
        }
        
        setMessage('Email account successfully deleted.');
      } catch (error) {
        console.error('Fehler beim Löschen des E-Mail-Kontos:', error);
        setMessage(`Error: ${error.message}`);
      }
    };

    // Funktion zum Testen der E-Mail-Verbindung
    const handleTestConnection = async (account) => {
      if (!account) return;
      
      try {
        setMessage('Teste Verbindung...');
        
        // Sende eine Anfrage an die Cloud Function, um die Verbindung zu testen
        const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/testEmailConnection', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: account.email,
            password: account.password,
            imapHost: account.imap_host,
            imapPort: account.imap_port,
            smtpHost: account.smtp_host,
            smtpPort: account.smtp_port,
            encryptionType: account.encryption_type,
            smtpEncryption: account.smtp_encryption
          }),
        });
        
        const data = await response.json();
        
        if (data.success) {
          setMessage('Verbindung erfolgreich getestet!');
        } else {
          setMessage(`Verbindungsfehler: ${data.error}`);
        }
      } catch (error) {
        console.error('Fehler beim Testen der Verbindung:', error);
        setMessage(`Fehler: ${error.message}`);
      }
    };

    // Funktion zum Aktualisieren der Werte des ausgewählten Kontos
    const handleAccountChange = (accountId, e) => {
      const { name, value } = e.target;
      
      // Bei queue_duration sicherstellen, dass wir einen gültigen numerischen Wert haben
      const processedValue = name === 'queue_duration' ? 
        (parseInt(value) || 10) : value;
      
      setEmailAccounts(prev => {
        return prev.map(account => {
          if (account.id === accountId) {
            return {
              ...account,
              [name]: processedValue
            };
          }
          return account;
        });
      });
      
      // Aktualisiere auch selectedAccount wenn es das gleiche Konto ist
      if (selectedAccount && selectedAccount.id === accountId) {
        setSelectedAccount(prev => ({
          ...prev,
          [name]: processedValue
        }));
      }
    };

    // Funktion zum Aktualisieren der Werte des neuen Kontos
    const handleNewAccountChange = (e) => {
      const { name, value } = e.target;
      
      // Bei queue_duration sicherstellen, dass wir einen gültigen numerischen Wert haben
      const processedValue = name === 'queue_duration' ? 
        (parseInt(value) || 10) : value;
      
      setNewAccount(prev => ({
        ...prev,
        [name]: processedValue
      }));
    };

    // Funktion zum Setzen der Server-Einstellungen basierend auf dem Anbieter
    const setServerSettings = (provider, isNewAccount = false) => {
      const settings = {
        hostinger: {
          imap_host: 'imap.hostinger.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.hostinger.com',
          smtp_port: '465',
          smtp_encryption: 'true'
        },
        outlook: {
          imap_host: 'outlook.office365.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.office365.com',
          smtp_port: '587',
          smtp_encryption: 'false'
        }
      };
      
      if (isNewAccount) {
        setNewAccount(prev => ({
          ...prev,
          ...settings[provider]
        }));
      } else if (selectedAccount) {
        setSelectedAccount(prev => ({
          ...prev,
          ...settings[provider]
        }));
      }
    };

    // EmailLogsModal Komponente
    const EmailLogsModal = () => {
      if (!showLogsModal || !selectedAccountLogs) return null;
      
      const logs = emailLogs[selectedAccountLogs.email] || [];
      
      const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      };
      
      const getStatusIcon = (status) => {
        switch (status) {
          case 'success':
            return '✅';
          case 'error':
            return '❌';
          case 'processing':
            return '⏳';
          case 'info':
            return 'ℹ️';
          default:
            return '📧';
        }
      };
      
      const getStatusColor = (status) => {
        switch (status) {
          case 'success':
            return '#10b981';
          case 'error':
            return '#ef4444';
          case 'processing':
            return '#f59e0b';
          case 'info':
            return '#3b82f6';
          default:
            return '#6b7280';
        }
      };
      
      return (
        <div className="emailLogsModalOverlay" onClick={() => setShowLogsModal(false)}>
          <div className="emailLogsModalContainer" onClick={(e) => e.stopPropagation()}>
            <div className="emailLogsModalHeader">
              <h3>📧 Email Logs: {selectedAccountLogs.email}</h3>
              <button 
                className="emailLogsModalCloseButton"
                onClick={() => setShowLogsModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="emailLogsModalContent">
              {logs.length === 0 ? (
                <div className="emailLogsEmpty">
                  <div className="emailLogsEmptyIcon">📭</div>
                  <p>No logs available for this email account.</p>
                  <small>Logs will appear here after sending emails.</small>
                </div>
              ) : (
                <div className="emailLogsList">
                  {logs.map((log) => (
                    <div 
                      key={log.id} 
                      className={`emailLogItem ${log.status}`}
                      style={{ borderLeftColor: getStatusColor(log.status) }}
                    >
                      <div className="emailLogHeader">
                        <div className="emailLogStatus">
                          <span className="emailLogStatusIcon">{getStatusIcon(log.status)}</span>
                          <span className="emailLogStatusText" style={{ color: getStatusColor(log.status) }}>
                            {log.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="emailLogDate">
                          {formatDate(log.timestamp)}
                        </div>
                      </div>
                      
                      <div className="emailLogMessage">
                        {log.message}
                      </div>
                      
                      {log.recipientEmail && (
                        <div className="emailLogDetail">
                          <strong>To:</strong> {log.recipientEmail}
                        </div>
                      )}
                      
                      {log.subject && (
                        <div className="emailLogDetail">
                          <strong>Subject:</strong> {log.subject}
                        </div>
                      )}
                      
                      {log.emailType && (
                        <div className="emailLogDetail">
                          <strong>Type:</strong> {log.emailType}
                        </div>
                      )}
                      
                      {log.duration && (
                        <div className="emailLogDetail">
                          <strong>Duration:</strong> {log.duration}ms
                        </div>
                      )}
                      
                      {log.messageId && (
                        <div className="emailLogDetail">
                          <strong>Message ID:</strong> {log.messageId}
                        </div>
                      )}
                      
                      {log.errorCode && (
                        <div className="emailLogDetail emailLogError">
                          <strong>Error Code:</strong> {log.errorCode}
                        </div>
                      )}
                      
                      {log.errorResponse && (
                        <div className="emailLogDetail emailLogError">
                          <strong>Error Response:</strong> {log.errorResponse}
                        </div>
                      )}
                      
                      {log.previewUrl && (
                        <div className="emailLogDetail">
                          <strong>Preview:</strong> 
                          <a href={log.previewUrl} target="_blank" rel="noopener noreferrer">
                            View Email
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="settingsGroup1">
        <h3>Manage Email Accounts</h3>
        
        {isLoading ? (
          <p></p>
        ) : (
          <>
            <div className="emailAccountsList">
              {emailAccounts.length === 0 ? (
                <div className="noEmailAccounts">
                  <div className="noEmailAccounts-content">
                    <div className="noEmailAccounts-icon">📧</div>
                    <h4>No Email Accounts Configured</h4>
                    <p>Please add your first email account to start sending emails.</p>
                    <button 
                      className="addEmailAccountButton primary-button"
                      onClick={() => setShowAddAccount(true)}
                    >
                      + Add Email Account
                    </button>
                  </div>
                </div>
              ) : (
                emailAccounts.map((account, index) => (
                  <div 
                    key={index} 
                    className={`emailAccountItem ${selectedAccount && selectedAccount.id === account.id ? 'selected' : ''}`}
                    onClick={() => setSelectedAccount(selectedAccount && selectedAccount.id === account.id ? null : account)}
                  >
                    <div className="emailAccountInfo">
                      <div className="emailAccountAddress">
                        {account.email}
                        <span className={`emailAccountStatus ${getAccountLogStatus(account.email)}`}>
                          {getAccountLogStatus(account.email) === 'success' && <img src="/img/mail_check.svg" alt="Success" style={{width: '20px', height: '20px', marginBottom: '-5px'}} />}
                          {getAccountLogStatus(account.email) === 'error' && <img src="/img/mail_warning.svg" alt="Warning" style={{width: '20px', height: '20px', marginBottom: '-5px'}} />}
                          {getAccountLogStatus(account.email) === 'info' && <img src="/img/mail_info.svg" alt="Info" style={{width: '20px', height: '20px', marginBottom: '-5px'}} />}
                        </span>
                      </div>
                      <div className="emailAccountServer">{account.smtp_host}</div>
                    </div>
                    <div className="emailAccountActions">
                      <button 
                        className="emailAccountLogsButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowLogs(account);
                        }}
                        title="View Logs"
                      >
                        Logs
                      </button>
                      <button 
                        className="emailAccountDeleteButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAccount(account);
                        }}
                        title="Delete Account"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Button zum Hinzufügen weiterer Konten, wenn bereits Konten vorhanden sind */}
            {emailAccounts.length > 0 && !showAddAccount && (
              <button 
                className="addEmailAccountButton"
                style={{ marginBottom: '15px' }}
                onClick={() => {
                  setSelectedAccount(null); // Deselektiere das aktuell ausgewählte Konto
                  setShowAddAccount(true);
                }}
              >
                + Add More Email Accounts
              </button>
            )}
            
            {message && <div className="emailAccountMessage">{message}</div>}
            
            {/* Dark overlay background for modal */}
            {(selectedAccount || showAddAccount) && (
              <div 
                className="emailAccountModalOverlay"
                onClick={() => {
                  setSelectedAccount(null);
                  setShowAddAccount(false);
                }}
              />
            )}
            
            {selectedAccount && (
              <div className="emailAccountDetails" onClick={(e) => e.stopPropagation()}>
                <div className="emailAccountDetailsHeader">
                  <h4>Edit Email Account</h4>
                  <button 
                    className="emailAccountDetailsClose"
                    onClick={() => setSelectedAccount(null)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="emailAccountDetailsContent">
                  <div className="settingField">
                    <label>E-Mail Address:</label>
                    <input
                      type="email"
                      name="email"
                      value={selectedAccount.email}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Password:</label>
                    <input
                      type="password"
                      name="password"
                      value={selectedAccount.password}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="Your email password"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Sender Name:</label>
                    <input
                      type="text"
                      name="sender_name"
                      value={selectedAccount.sender_name || ''}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="Name displayed as sender"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label htmlFor="queue_duration">Delay between emails (in minutes):</label>
                    <input
                      type="number"
                      name="queue_duration"
                      value={selectedAccount.queue_duration || '10'}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      min="1"
                      max="1440"
                      className="queue-duration-input"
                    />
                    <div className="delayHint">
                      This setting defines the base time in minutes between emails sent from this account.
                      To reduce spam risk and appear more natural, the actual delay will be randomly varied by ±30%.
                      For example, with a setting of 30 minutes, the actual delay can be between 21 and 39 minutes.
                      A new random value is generated for each email.
                    </div>
                  </div>
                  
                  <div className="settingField">
                    <label>Quiet hours (no emails during this period):</label>
                    <div className="quietHoursHint">
                      Times are based on German time zone (UTC+1 in winter, UTC+2 in summer).
                    </div>
                    <div className="sleepTimeInputs">
                      <div>
                        <label>From:</label>
                        <input
                          type="time"
                          name="sleep_time_start"
                          value={selectedAccount.sleep_time_start || '22:00'}
                          onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                        />
                      </div>
                      <div>
                        <label>To:</label>
                        <input
                          type="time"
                          name="sleep_time_end"
                          value={selectedAccount.sleep_time_end || '08:00'}
                          onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                        />
                      </div>
                    </div>
                    <div className="delayHint">
                      No emails will be sent during this time period. If a scheduled email falls within these hours,
                      it will automatically be delayed until the end of the quiet period. This is particularly useful
                      to avoid nighttime sending or to respect business hours.
                    </div>
                    {selectedAccount.sleep_time_end === "00:00" && (
                      <div className="warningMessage" style={{
                        color: '#e74c3c',
                        backgroundColor: '#fadbd8',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '8px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ marginRight: '8px', fontWeight: 'bold' }}>⚠️</span>
                        <span>
                          <strong>Achtung:</strong> Die Endzeit 00:00 (Mitternacht) kann zu Problemen bei der E-Mail-Zustellung führen. 
                          Bitte verwenden Sie stattdessen 23:59 oder 00:01 als Endzeit.
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="emailServerSettings">
                    <div className="emailServerTabs">
                      <button 
                        className="emailServerTab" 
                        onClick={() => setServerSettings('hostinger')}
                      >
                        Hostinger (Standard)
                      </button>
                      <button 
                        className="emailServerTab" 
                        onClick={() => setServerSettings('outlook')}
                      >
                        Outlook
                      </button>
                    </div>
                  </div>
                  
                  <h4>IMAP Settings (for email reception)</h4>
                  
                  <div className="settingField">
                    <label>IMAP-Host:</label>
                    <input
                      type="text"
                      name="imap_host"
                      value={selectedAccount.imap_host}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="imap.example.com"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>IMAP-Port:</label>
                    <input
                      type="text"
                      name="imap_port"
                      value={selectedAccount.imap_port}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="993"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Encryption Type:</label>
                    <select
                      name="encryption_type"
                      value={selectedAccount.encryption_type}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                    >
                      <option value="SSL">SSL</option>
                      <option value="TLS">TLS</option>
                      <option value="STARTTLS">STARTTLS</option>
                    </select>
                  </div>
                  
                  <h4>SMTP Settings (for email sending)</h4>
                  
                  <div className="settingField">
                    <label>SMTP-Host:</label>
                    <input
                      type="text"
                      name="smtp_host"
                      value={selectedAccount.smtp_host}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="smtp.example.com"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>SMTP-Port:</label>
                    <input
                      type="text"
                      name="smtp_port"
                      value={selectedAccount.smtp_port}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                      placeholder="587"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>SMTP-Encryption:</label>
                    <select
                      name="smtp_encryption"
                      value={selectedAccount.smtp_encryption}
                      onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                    >
                      <option value="false">None/STARTTLS</option>
                      <option value="true">SSL/TLS</option>
                    </select>
                  </div>
                </div>
                
                <div className="emailAccountButtons">
                  <button 
                    className="cancelEmailAccountButton"
                    onClick={() => setSelectedAccount(null)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="saveEmailAccountButton"
                    onClick={handleUpdateAccount}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
            
            {showAddAccount && (
              <div className="emailAccountDetails" onClick={(e) => e.stopPropagation()}>
                <div className="emailAccountDetailsHeader">
                  <h4>Add New Email Account</h4>
                  <button 
                    className="emailAccountDetailsClose"
                    onClick={() => setShowAddAccount(false)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="emailAccountDetailsContent">
                  <div className="settingField">
                    <label>E-Mail Address:</label>
                    <input
                      type="email"
                      name="email"
                      value={newAccount.email}
                      onChange={handleNewAccountChange}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Password:</label>
                    <input
                      type="password"
                      name="password"
                      value={newAccount.password}
                      onChange={handleNewAccountChange}
                      placeholder="Your email password"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Sender Name:</label>
                    <input
                      type="text"
                      name="sender_name"
                      value={newAccount.sender_name || ''}
                      onChange={handleNewAccountChange}
                      placeholder="Name displayed as sender"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label htmlFor="queue_duration">Delay between emails (in minutes):</label>
                    <input
                      type="number"
                      name="queue_duration"
                      value={newAccount.queue_duration || '10'}
                      onChange={handleNewAccountChange}
                      min="1"
                      max="1440"
                      className="queue-duration-input"
                    />
                    <div className="delayHint">
                      This setting defines the base time in minutes between emails sent from this account.
                      To reduce spam risk and appear more natural, the actual delay will be randomly varied by ±30%.
                      For example, with a setting of 30 minutes, the actual delay can be between 21 and 39 minutes.
                      A new random value is generated for each email.
                    </div>
                  </div>
                  
                  <div className="settingField">
                    <label>Quiet hours (no emails during this period):</label>
                    <div className="quietHoursHint">
                      Times are based on German time zone (UTC+1 in winter, UTC+2 in summer).
                    </div>
                    <div className="sleepTimeInputs">
                      <div>
                        <label>From:</label>
                        <input
                          type="time"
                          name="sleep_time_start"
                          value={newAccount.sleep_time_start || '22:00'}
                          onChange={handleNewAccountChange}
                        />
                      </div>
                      <div>
                        <label>To:</label>
                        <input
                          type="time"
                          name="sleep_time_end"
                          value={newAccount.sleep_time_end || '08:00'}
                          onChange={handleNewAccountChange}
                        />
                      </div>
                    </div>
                    <div className="delayHint">
                      No emails will be sent during this time period. If a scheduled email falls within these hours, 
                      it will automatically be delayed until the end of the quiet period. This is particularly useful
                      to avoid nighttime sending or to respect business hours.
                    </div>
                    {newAccount.sleep_time_end === "00:00" && (
                      <div className="warningMessage" style={{
                        color: '#e74c3c',
                        backgroundColor: '#fadbd8',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '8px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ marginRight: '8px', fontWeight: 'bold' }}>⚠️</span>
                        <span>
                          <strong>Warning:</strong> The time 00:00 (Midnight) can cause problems with email delivery. 
                          Please use 23:59 or 00:01 instead.
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="emailServerSettings">
                    <div className="emailServerTabs">
                      <button 
                        className="emailServerTab" 
                        onClick={() => setServerSettings('hostinger', true)}
                      >
                        Hostinger (Standard)
                      </button>
                      <button 
                        className="emailServerTab" 
                        onClick={() => setServerSettings('outlook', true)}
                      >
                        Outlook
                      </button>
                    </div>
                  </div>
                  
                  <h4>IMAP Settings (for email reception)</h4>
                  
                  <div className="settingField">
                    <label>IMAP-Host:</label>
                    <input
                      type="text"
                      name="imap_host"
                      value={newAccount.imap_host}
                      onChange={handleNewAccountChange}
                      placeholder="imap.example.com"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>IMAP-Port:</label>
                    <input
                      type="text"
                      name="imap_port"
                      value={newAccount.imap_port}
                      onChange={handleNewAccountChange}
                      placeholder="993"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>Encryption Type:</label>
                    <select
                      name="encryption_type"
                      value={newAccount.encryption_type}
                      onChange={handleNewAccountChange}
                    >
                      <option value="SSL">SSL</option>
                      <option value="TLS">TLS</option>
                      <option value="STARTTLS">STARTTLS</option>
                    </select>
                  </div>
                  
                  <h4>SMTP Settings (for email sending)</h4>
                  
                  <div className="settingField">
                    <label>SMTP-Host:</label>
                    <input
                      type="text"
                      name="smtp_host"
                      value={newAccount.smtp_host}
                      onChange={handleNewAccountChange}
                      placeholder="smtp.example.com"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>SMTP-Port:</label>
                    <input
                      type="text"
                      name="smtp_port"
                      value={newAccount.smtp_port}
                      onChange={handleNewAccountChange}
                      placeholder="587"
                    />
                  </div>
                  
                  <div className="settingField">
                    <label>SMTP-Encryption:</label>
                    <select
                      name="smtp_encryption"
                      value={newAccount.smtp_encryption}
                      onChange={handleNewAccountChange}
                    >
                      <option value="false">None/STARTTLS</option>
                      <option value="true">SSL/TLS</option>
                    </select>
                  </div>
                </div>
                
                <div className="emailAccountButtons">
                  <button 
                    className="cancelEmailAccountButton"
                    onClick={() => {
                      setShowAddAccount(false);
                      setNewAccount({
                        email: '',
                        password: '',
                        imap_host: 'imap.hostinger.com',
                        imap_port: '993',
                        encryption_type: 'SSL',
                        smtp_host: 'smtp.hostinger.com',
                        smtp_port: '465',
                        smtp_encryption: 'true',
                        sender_name: 'BonusClub',
                        queue_duration: '10',
                        sleep_time_start: '22:00',
                        sleep_time_end: '08:00'
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="saveEmailAccountButton"
                    onClick={handleAddAccount}
                  >
                    Add Account
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Email Logs Modal */}
        <EmailLogsModal />
      </div>
    );
  };

  const renderEmailTab = () => (
    <div className="settingsGroup1">
      <EmailAccountManager userId={userId} />
    </div>
  );

  const renderEmailPreferencesTab = () => (
    <div className="emailFooterSettingsSection">
      <h3>Email Footer</h3>
      <div className="emailFooter-settingsGrid">
        <div className="emailFooter-settingsLeft">
          <div className="settingField">
            <label>Footer Font:</label>
            <select
              name="footer_font"
              value={emailSettings.footer_font || "Helvetica Neue"}
              onChange={handleChange}
              className="fontSelect"
              style={{padding: '10px 10px'}}
            >
              <option value="Helvetica Neue">Helvetica Neue</option>
              <option value="Arial">Arial</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Times New Roman">Times New Roman</option>
            </select>
            <p className="settingHelp">
              Choose a font for your email footer. Only fonts supported by most email clients are shown.
            </p>
          </div>
          <div className="settingField">
            <label>Image Size: {emailSettings.footer_image_size}px</label>
            <div className="emailFooter-sliderContainer">
              <input
                type="range"
                min="40"
                max="200"
                style={{width: '100%', height: '5px', background: 'linear-gradient(90deg,rgb(193, 217, 249) 0%,rgb(213, 228, 244) 100%)', border:'none'}}
                value={emailSettings.footer_image_size}
                onChange={(e) => setEmailSettings(prev => ({
                  ...prev, 
                  footer_image_size: parseInt(e.target.value)
                }))}
                className="emailFooter-sizeSlider"
              />
              <div className="emailFooter-sliderLabels">
                <span>Small</span>
                <span>Large</span>
              </div>
            </div>
            <p className="settingHelp">
              Adjust the size of your logo in the email footer.
            </p>
          </div>
          <div className="settingField">
            <label>Image Position:</label>
            <div className="emailFooter-positionToggle">
              <button 
                type="button"
                className={`emailFooter-positionButton ${emailSettings.footer_image_position === 'below' ? 'active' : ''}`}
                onClick={() => setEmailSettings(prev => ({...prev, footer_image_position: 'below'}))}
              >
                <span className="emailFooter-positionIconCircle">
                  <img src={downArrow} alt="down" className="emailFooter-positionIconSvg" />
                </span>
                Below Text
              </button>
              <button 
                type="button"
                className={`emailFooter-positionButton ${emailSettings.footer_image_position === 'beside' ? 'active' : ''}`}
                onClick={() => setEmailSettings(prev => ({...prev, footer_image_position: 'beside'}))}
              >
                <span className="emailFooter-positionIconCircle rotateLeft">
                  <img src={downArrow} alt="left" className="emailFooter-positionIconSvg" />
                </span>
                Left of Text
              </button>
            </div>
            <p className="settingHelp">
              Choose whether to display the image below or beside the text.
            </p>
          </div>
          <div className="settingField">
            <label>Footer Image:</label>
            <div 
              className="imageDropZone"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={handleImageDrop}
            >
              {emailSettings.footer_image_url ? (
                <div className="currentImageContainer">
                  <img 
                    src={emailSettings.footer_image_url} 
                    alt="Footer Image" 
                    className="currentImage"
                  />
                  <button 
                    className="removeImageButton"
                    onClick={() => {
                      setEmailSettings(prev => ({
                        ...prev,
                        footer_image_url: ""
                      }));
                    }}
                    type="button"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="dropZoneContent">
                  <div className="dropZoneIcon">📷</div>
                  <div className="dropZoneText">
                    Drag image here or click to select
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg, image/png, image/gif, image/svg+xml"
                onChange={handleImageUpload}
                disabled={uploading}
                className="fileInput"
              />
              {uploading && (
                <div className="uploadingOverlay">
                  <div className="uploadingSpinner"></div>
                  <div>Uploading...</div>
                </div>
              )}
            </div>
            <p className="settingHelp">
              This image will be displayed in the footer of your emails (e.g. your logo).
            </p>
          </div>
        </div>
        <div className="emailFooter-settingsRight">
          <div className="footerPreview">
            <h5>Footer Preview: <span className="fontInfo">Font: {emailSettings.footer_font}</span></h5>
            <div className="emailFooter-previewWrapper" style={{background: '#fff'}}>
              <iframe
                title="Email Footer Preview"
                className="emailFooter-previewFrame"
                srcDoc={`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <meta charset=\"UTF-8\">
                    <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\">
                    <style>
                      body {
                        font-family: 'Jost', Helvetica, Arial, sans-serif;
                        padding: 10px;
                        background: #fff;
                      }
                      a {
                        color: #4a90e2;
                        text-decoration: none;
                      }
                      a:hover {
                        text-decoration: underline;
                      }
                    </style>
                  </head>
                  <body>
                    ${generateEmailFooter(emailSettings)}
                  </body>
                  </html>
                `}
                frameBorder="0"
              />
            </div>
          </div>
          <div className="settingField">
            <label style={{marginTop: '30px'}}>Footer Text:</label>
            <div className="footerEditorContainer">
              <div className="emailFooter-editorToolbar">
                <button type="button" onClick={() => formatText('bold')} title="Bold">
                  <strong>B</strong>
                </button>
                <button type="button" onClick={() => formatText('italic')} title="Italic">
                  <em>I</em>
                </button>
                <button type="button" onClick={() => formatText('underline')} title="Underline">
                  <u>U</u>
                </button>
                <select 
                  onChange={(e) => {
                    if (e.target.value) {
                      formatText('fontSize', e.target.value);
                    }
                    e.target.value = '';
                  }}
                  title="Font size"
                  className="emailFooter-fontSizeSelect"
                >
                  <option value="">Size</option>
                  <option value="1">10px</option>
                  <option value="2">12px</option>
                  <option value="3">14px</option>
                  <option value="4">16px</option>
                  <option value="5">18px</option>
                  <option value="6">24px</option>
                  <option value="7">32px</option>
                </select>
                <div className="emailFooter-colorPickerContainer">
                  <input 
                    type="color" 
                    style={{width: '20px', height: '20px'}}
                    className="emailFooter-colorPicker" 
                    onChange={(e) => formatText('foreColor', e.target.value)}
                    title="Text color"
                  />
                  <span className="emailFooter-colorPickerLabel">Color</span>
                </div>
                <button type="button" onClick={() => formatText('insertUnorderedList')} title="List">
                  • List
                </button>
                <button type="button" onClick={() => formatText('insertOrderedList')} title="Numbered List">
                  1. List
                </button>
                <button type="button" onClick={() => formatText('createLink', prompt('Enter URL:'))} title="Insert Link">
                  🔗
                </button>
                <button type="button" onClick={() => formatText('removeFormat')} title="Remove Format">
                  Text  
                </button>
              </div>
              <div
                ref={editorRef}
                className="footerHtmlEditor"
                contentEditable
                dangerouslySetInnerHTML={{ __html: emailSettings.footer_text }}
                onBlur={(e) => {
                  setEmailSettings(prev => ({
                    ...prev,
                    footer_text: e.target.innerHTML
                  }));
                }}
              />
              <div className="footerEditorToggle">
                <button 
                  type="button" 
                  onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                  className={showHtmlEditor ? 'active' : ''}
                >
                  {showHtmlEditor ? 'Close Editor' : 'Edit HTML'}
                </button>
              </div>
              {showHtmlEditor && (
                <textarea
                  className="footerHtmlTextarea"
                  value={emailSettings.footer_text}
                  onChange={(e) => {
                    setEmailSettings(prev => ({
                      ...prev,
                      footer_text: e.target.value
                    }));
                  }}
                  rows={6}
                  placeholder="Enter HTML code"
                />
              )}
            </div>
            <p className="settingHelp">
              This text will be displayed at the end of each email. You can format the text or enter direct HTML code.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAiTab = () => (
    <div className="settingsGroup">
      <h3>AI Settings</h3>
      
      <div className="settingField emailPromptField">
        <label><strong>Brand Guidelines</strong> <span className="fieldDescription">Your AI's personality and tone</span></label>
        <textarea
          name="promptTemplate"
          value={aiSettings.promptTemplate || ""}
          onChange={handleAiChange}
          className="aiPromptTextarea emailPromptTextarea"
          rows="12"
          placeholder="Define who your AI is, how it should sound, and your brand personality..."
        />
        <div className="settingHelp">
          <p>
            <strong>This defines how your AI presents your brand in emails.</strong> Think of it as setting the character and voice behind your messages.
          </p>
          
          <div className="infoContainers">
            <div className={`infoContainer ${expandedSections['brand-tips'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('brand-tips')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">💡</span>
                  Tips for Better Emails
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['brand-tips'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Define a clear personality that matches your brand. For example:</p>
                <p><i>"You are a friendly hospitality expert who helps businesses increase their online visibility through authentic reviews. You're enthusiastic but professional, with 5+ years of experience in the hospitality industry. You understand the challenges small businesses face and speak with authority but remain approachable."</i></p>
                <p>Include these elements in your brand guidelines:</p>
                <ul>
                  <li><strong>Identity:</strong> Specify exactly who the AI represents (name, role, company)</li>
                  <li><strong>Expertise level:</strong> Define areas of knowledge and experience</li>
                  <li><strong>Personality traits:</strong> List 3-5 key characteristics (friendly, professional, direct)</li>
                  <li><strong>Background:</strong> Add relevant experience or context</li>
                </ul>
              </div>
            </div>
            
            <div className={`infoContainer ${expandedSections['brand-include'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('brand-include')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">✓</span>
                  What to Include
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['brand-include'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Your brand guidelines should clearly define:</p>
                <ul>
                  <li><strong>Voice & perspective:</strong> First-person as yourself or your company representative</li>
                  <li><strong>Industry knowledge:</strong> Specific terminology or concepts the AI should understand</li>
                  <li><strong>Communication style:</strong> How formal/casual, use of humor, level of directness</li>
                  <li><strong>Value proposition:</strong> Core benefits you provide to clients</li>
                  <li><strong>Taboo topics:</strong> Subjects or approaches to avoid</li>
                  <li><strong>Relationship approach:</strong> How to build rapport with recipients</li>
                  <li><strong>Problem-solving style:</strong> How to address challenges or objections</li>
                  <li><strong>Company values:</strong> Key principles that should shine through</li>
                  <li><strong>Target audience understanding:</strong> How to speak to your specific customer base</li>
                </ul>
                <p><strong>Example:</strong> <i>"You're writing as Sarah from BonusClub. You're knowledgeable about hotel marketing and online reputation management. Use hospitality industry terms naturally. Be friendly but professional, avoid overly casual language. Focus on how authentic guest experiences improve visibility and bookings. Never criticize competitors directly. Show empathy for the challenges of small business owners. Emphasize authenticity and trust as core values."</i></p>
              </div>
            </div>
            
            <div className={`infoContainer ${expandedSections['brand-voice'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('brand-voice')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">🎯</span>
                  Brand Voice Elements
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['brand-voice'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Fine-tune your brand voice with these specific elements:</p>
                <ul>
                  <li><strong>Formality spectrum:</strong> Define exactly where you fall between casual and formal
                    <ul>
                      <li>Very casual: <i>"Hey there! Just checking in..."</i></li>
                      <li>Professional but friendly: <i>"Hello Julia, I wanted to follow up on our conversation..."</i></li>
                      <li>Formal: <i>"Dear Mr. Thompson, I am writing regarding our previous discussion..."</i></li>
                    </ul>
                  </li>
                  <li><strong>Technical language:</strong> Specify how much industry jargon to use
                    <ul>
                      <li>Minimal: <i>"We help improve your online visibility"</i></li>
                      <li>Moderate: <i>"We boost your search ranking through authentic review generation"</i></li>
                      <li>Technical: <i>"We optimize your SEO metrics through strategic UGC acquisition"</i></li>
                    </ul>
                  </li>
                  <li><strong>Sentence structure:</strong> Short and direct vs. more elaborate</li>
                  <li><strong>Cultural references:</strong> Whether to include them and what type</li>
                  <li><strong>Emotional tone:</strong> Enthusiastic, measured, empathetic, authoritative</li>
                  <li><strong>Metaphors & analogies:</strong> Types that align with your brand</li>
                  <li><strong>Humor level:</strong> None, subtle, conversational, playful</li>
                  <li><strong>Confidence level:</strong> Tentative, balanced, authoritative</li>
                </ul>
                <p><strong>Pro tip:</strong> Include examples of phrases your brand would and wouldn't use to make your guidelines crystal clear.</p>
                <div className="examples-box">
                  <p><strong>Would say:</strong> <i>"I noticed you've built an impressive collection of guest reviews over the past year."</i></p>
                  <p><strong>Wouldn't say:</strong> <i>"Your online presence is lacking compared to competitors."</i></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => resetToDefaultTemplate()} 
          className="resetTemplateButton"
        >
          <span className="buttonIcon">↺</span> Reset to Default
        </button>
      </div>
      
      <div className="settingField emailPromptField">
        <label><strong>Email Guidelines</strong> <span className="fieldDescription">Structure and formatting rules</span></label>
        <textarea
          name="strictInstructions"
          value={aiSettings.strictInstructions || ""}
          onChange={handleAiChange}
          className="aiPromptTextarea emailPromptTextarea"
          rows="12"
          placeholder="Add specific instructions for email format and structure, starting each with '- '..."
        />
        <div className="settingHelp">
          <p>
            <strong>These rules determine how your emails are structured and formatted.</strong> They help ensure consistency across all generated messages.
          </p>
          
          <div className="infoContainers">
            <div className={`infoContainer ${expandedSections['email-format'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('email-format')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">📝</span>
                  Format Instructions
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['email-format'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Write each instruction on a new line starting with "- " like:</p>
                <p><i>- Start with a warm greeting that includes the recipient's first name<br/>- Keep paragraphs to 2-3 sentences maximum<br/>- End with a clear call to action that specifies exactly what you want them to do</i></p>
                <p><strong>Important:</strong> These instructions are strictly followed by the AI, so be precise about your requirements.</p>
                <p>Use these format instructions to control:</p>
                <ul>
                  <li><strong>Email length:</strong> Number of paragraphs or sentences</li>
                  <li><strong>Paragraph structure:</strong> Short vs. detailed</li>
                  <li><strong>Text formatting:</strong> Use of bullet points, numbered lists, bold/italic</li>
                  <li><strong>Information presentation:</strong> How to format prices, dates, offers</li>
                  <li><strong>White space usage:</strong> Spacing between paragraphs</li>
                  <li><strong>Sentence complexity:</strong> Simple or compound sentences</li>
                  <li><strong>Question usage:</strong> When and how to include questions</li>
                  <li><strong>Emphasis techniques:</strong> How to highlight key points</li>
                </ul>
                <p><strong>Example set of instructions:</strong></p>
                <div className="example-instructions">
                  <p>- Always start with "Hi [First Name]," followed by a personal observation<br/>
                  - Keep the entire email under 150 words<br/>
                  - Use exactly 3 paragraphs: introduction, value proposition, call-to-action<br/>
                  - Include one question in the middle of the email<br/>
                  - Use bullet points only when listing 3+ benefits<br/>
                  - End with a specific, time-bound call to action</p>
                </div>
              </div>
            </div>
            
            <div className={`infoContainer ${expandedSections['email-examples'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('email-examples')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">⭐</span>
                  Examples of Good Rules
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['email-examples'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p><strong>Effective rules are specific and actionable:</strong></p>
                <ul>
                  <li>- Always address recipient by first name, never use "Dear Sir/Madam"</li>
                  <li>- Keep emails between 4-6 sentences total</li>
                  <li>- First paragraph must mention a specific detail about their business</li>
                  <li>- Include exactly one question in each email</li>
                  <li>- Never use exclamation marks or ALL CAPS for emphasis</li>
                  <li>- Always include a specific call-to-action in the final sentence</li>
                  <li>- Never mention prices or discounts in the first email</li>
                  <li>- Use 1-2 short sentences per paragraph maximum</li>
                  <li>- Always reference their website or online presence specifically</li>
                  <li>- Include one brief industry statistic or trend in each email</li>
                  <li>- Use the word "authentic" at least once in every email</li>
                  <li>- Never make promises about specific results or numbers</li>
                  <li>- Avoid phrases like "I hope this email finds you well"</li>
                  <li>- End with a question that requires more than a yes/no answer</li>
                </ul>
                <p><strong>Avoid vague rules</strong> like "be friendly" or "write professionally" - instead, give concrete examples of what that means to you.</p>
                <div className="examples-comparison">
                  <div className="bad-example">
                    <p><strong>Too vague:</strong> "Write in a professional manner"</p>
                  </div>
                  <div className="good-example">
                    <p><strong>Specific:</strong> "Use industry terminology but explain any technical concepts. Avoid slang, contractions, and exclamation points."</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`infoContainer ${expandedSections['email-structure'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('email-structure')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">🔄</span>
                  Structure Elements
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['email-structure'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Create a consistent email structure by defining rules for each section:</p>
                
                <p><strong>1. Introduction</strong></p>
                <ul>
                  <li>Greeting style (formal vs. casual)</li>
                  <li>Personal connection or context (reference to previous contact)</li>
                  <li>Purpose statement (why you're writing)</li>
                  <li>Example: <i>- Start with "Hi [Name]," followed by a brief personal reference or compliment about their business</i></li>
                </ul>
                
                <p><strong>2. Main Message</strong></p>
                <ul>
                  <li>Key points to include (value proposition, benefits)</li>
                  <li>Supporting details (examples, statistics)</li>
                  <li>Logical flow between points</li>
                  <li>Example: <i>- Present exactly one benefit in each paragraph, with the most important benefit first</i></li>
                </ul>
                
                <p><strong>3. Call-to-Action</strong></p>
                <ul>
                  <li>Specific action you want recipient to take</li>
                  <li>Level of urgency/directness</li>
                  <li>Follow-up expectations</li>
                  <li>Example: <i>- End with a clear question that requires a response, such as "Would you be available for a 15-minute call next week?"</i></li>
                </ul>
                
                <p><strong>4. Sign-off Style</strong></p>
                <ul>
                  <li>Closing phrase (or none)</li>
                  <li>Signature format</li>
                  <li>Contact information to include</li>
                  <li>Example: <i>- No closing phrase, end with the last content sentence</i></li>
                </ul>
                
                <p><strong>Complete Structure Example:</strong></p>
                <div className="structure-example">
                  <p><strong>Introduction:</strong> Personal greeting + specific observation about their business</p>
                  <p><strong>Context:</strong> Brief explanation of why you're reaching out</p>
                  <p><strong>Value:</strong> One key benefit your service provides to businesses like theirs</p>
                  <p><strong>Proof:</strong> Brief example or statistic that supports your claim</p>
                  <p><strong>Action:</strong> Clear, specific next step with timeframe</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => resetStrictInstructions()} 
          className="resetTemplateButton"
        >
          <span className="buttonIcon">↺</span> Reset to Default
        </button>
      </div>
      
      <div className="settingField emailPromptField">
        <label><strong>AI Cleanup Guidelines</strong> <span className="fieldDescription">Rules for filtering and cleaning contact lists</span></label>
        <textarea
          name="cleanupGuidelines"
          value={aiSettings.cleanupGuidelines || ""}
          onChange={handleAiChange}
          className="aiPromptTextarea emailPromptTextarea"
          rows="10"
          placeholder="Add specific instructions for content refinement, starting each with '- '..."
        />
        <div className="settingHelp">
          <p>
            <strong>These guidelines help filter and clean your contact lists</strong> by specifying criteria for which contacts to keep and which to remove.
          </p>
          
          <div className="infoContainers">
            <div className={`infoContainer ${expandedSections['cleanup-tips'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('cleanup-tips')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">🔍</span>
                  Cleanup Best Practices
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['cleanup-tips'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Effective cleanup guidelines help verify and refine content for specific contexts:</p>
                <ul>
                  <li><strong>Audience verification:</strong> Ensure content matches target audience characteristics</li>
                  <li><strong>Brand alignment:</strong> Check if content aligns with specific brand types</li>
                  <li><strong>Content authenticity:</strong> Verify that content feels genuine for the specified persona</li>
                  <li><strong>Industry specificity:</strong> Confirm industry-appropriate terminology and focus</li>
                  <li><strong>Demographic matching:</strong> Ensure content speaks to the right demographic group</li>
                </ul>
                <p><strong>Example guidelines:</strong></p>
                <p><i>- Make sure the content represents a female entrepreneur's personal brand<br/>
                - Verify the language appeals to luxury hotel owners aged 45-60<br/>
                - Ensure content focuses on sustainability for eco-friendly businesses<br/>
                - Check that tone matches a young, tech-savvy startup founder<br/>
                - Confirm content addresses pain points specific to small boutique hotels<br/>
                - Ensure language is appropriate for family-owned businesses<br/>
                - Verify content highlights unique value for independent property owners</i></p>
              </div>
            </div>
            
            <div className={`infoContainer ${expandedSections['cleanup-examples'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('cleanup-examples')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">✏️</span>
                  Before & After Examples
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['cleanup-examples'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p><strong>Example 1: Targeting female entrepreneurs</strong></p>
                <div className="examples-comparison">
                  <div className="bad-example">
                    <p><strong>Before:</strong> "Our platform helps business owners increase their online visibility through strategic review management, which can lead to improved conversion rates and business growth."</p>
                  </div>
                  <div className="good-example">
                    <p><strong>After:</strong> "As a woman who built her own brand from scratch, you know the value of authentic connections. Our platform helps you showcase your unique story through genuine guest experiences that resonate with your ideal clients."</p>
                  </div>
                </div>
                
                <p><strong>Example 2: Luxury hotel focus</strong></p>
                <div className="examples-comparison">
                  <div className="bad-example">
                    <p><strong>Before:</strong> "We can help you get more reviews to improve your ranking on booking platforms, which will help attract more customers to your property."</p>
                  </div>
                  <div className="good-example">
                    <p><strong>After:</strong> "We specialize in helping exclusive properties like yours cultivate a curated collection of reviews that highlight your exceptional service and unique amenities, attracting discerning guests who value premium experiences."</p>
                  </div>
                </div>
                
                <p><strong>Example 3: Family business approach</strong></p>
                <div className="examples-comparison">
                  <div className="bad-example">
                    <p><strong>Before:</strong> "Our data shows that businesses with more authentic reviews see a 30% increase in bookings on average."</p>
                  </div>
                  <div className="good-example">
                    <p><strong>After:</strong> "For multi-generation family businesses like yours, we understand that your reputation is your legacy. Our approach helps you showcase the personal touches and family traditions that make guests feel like part of your extended family."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => resetCleanupGuidelines()} 
          className="resetTemplateButton"
        >
          <span className="buttonIcon">↺</span> Reset to Default
        </button>
      </div>
      
      <div className="settingField emailPromptField">
        <label><strong>Reply Agent Guidelines</strong> <span className="fieldDescription">Rules for responding to incoming emails</span></label>
        <textarea
          name="replyAgentGuidelines"
          value={aiSettings.replyAgentGuidelines || ""}
          onChange={handleAiChange}
          className="aiPromptTextarea emailPromptTextarea"
          rows="10"
          placeholder="Add specific instructions for reply generation, starting each with '- '..."
        />
        <div className="settingHelp">
          <p>
            <strong>These guidelines control how the AI responds to incoming emails</strong> by defining the tone, style, and approach for automated replies.
          </p>
          
          <div className="infoContainers">
            <div className={`infoContainer ${expandedSections['reply-tips'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('reply-tips')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">💬</span>
                  Reply Best Practices
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['reply-tips'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p>Effective reply guidelines help create natural, contextual responses:</p>
                <ul>
                  <li><strong>Context awareness:</strong> Reference specific details from the incoming email</li>
                  <li><strong>Tone matching:</strong> Mirror the sender's communication style and enthusiasm</li>
                  <li><strong>Response timing:</strong> Acknowledge urgency levels appropriately</li>
                  <li><strong>Information hierarchy:</strong> Address the most important points first</li>
                  <li><strong>Action orientation:</strong> Provide clear next steps when appropriate</li>
                </ul>
                <p><strong>Example guidelines:</strong></p>
                <p><i>- If the sender uses casual language, respond in a similarly relaxed tone<br/>
                - Always acknowledge specific questions or requests mentioned in their email<br/>
                - When they express urgency, prioritize quick, direct responses<br/>
                - Match their level of detail - brief for brief, detailed for detailed<br/>
                - Reference their business name or specific details they shared<br/>
                - End with a question or clear call-to-action to continue the conversation</i></p>
              </div>
            </div>
            
            <div className={`infoContainer ${expandedSections['reply-examples'] ? 'expanded' : ''}`}>
              <div 
                className="infoContainer-header" 
                onClick={() => toggleSection('reply-examples')}
              >
                <h4 className="infoContainer-title">
                  <span className="infoIcon-circle">📧</span>
                  Reply Examples
                </h4>
                <span
                  className="infoContainer-toggle"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                    transform: expandedSections['reply-examples'] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <img src={downArrow} alt="arrow" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
              </div>
              <div className="infoContainer-content">
                <p><strong>Example 1: Enthusiastic inquiry</strong></p>
                <div class="examples-comparison">
                  <div class="bad-example">
                    <p><strong>Incoming:</strong> "Hi! I'm super excited about your service! Can we schedule a call ASAP? I have so many questions!"</p>
                  </div>
                  <div class="good-example">
                    <p><strong>Reply:</strong> "Hi Sarah! I love your enthusiasm - it's exactly the kind of energy that makes businesses thrive! I'd be happy to answer all your questions. How does tomorrow at 2 PM work for a quick call?"</p>
                  </div>
                </div>
                
                <p><strong>Example 2: Professional inquiry</strong></p>
                <div class="examples-comparison">
                  <div class="bad-example">
                    <p><strong>Incoming:</strong> "Dear BonusClub Team, I would like to request more information about your review management services for our boutique hotel. Please provide pricing details."</p>
                  </div>
                  <div class="good-example">
                    <p><strong>Reply:</strong> "Dear Michael, Thank you for your interest in our review management services. I'd be happy to provide detailed information about how we can help your boutique hotel enhance its online presence. Would you prefer a brief call to discuss your specific needs, or shall I send you our service overview first?"</p>
                  </div>
                </div>
                
                <p><strong>Example 3: Skeptical inquiry</strong></p>
                <div class="examples-comparison">
                  <div class="bad-example">
                    <p><strong>Incoming:</strong> "I've tried review services before and they didn't work. What makes you different? I'm not looking to waste more money."</p>
                  </div>
                  <div class="good-example">
                    <p><strong>Reply:</strong> "I completely understand your skepticism - you're right to be cautious after a disappointing experience. What sets us apart is our focus on authentic guest experiences rather than artificial reviews. Would you be open to a brief conversation about what went wrong before and how our approach differs?"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => resetReplyAgentGuidelines()} 
          className="resetTemplateButton"
        >
          <span className="buttonIcon">↺</span> Reset to Default
        </button>
      </div>
      
      <div className="aiModelSettings">
        
        <div className="settingField">
          <label>AI-Model:</label>
          <select 
            name="model" 
            value={aiSettings.model} 
            onChange={handleAiChange}
            className="modelSelect"
          >
            <option value="gpt-4o-mini">GPT-4o Mini</option>
            <option value="gpt-4.1-nano">GPT-4.1 Nano</option>
          </select>
        </div>
        
        <div className="settingField">
          <label>Language:</label>
          <select 
            name="language" 
            value={aiSettings.language || "English"} 
            onChange={handleAiChange}
            className="languageSelect"
            style={{height: '40px', padding: '0px 10px', paddingRight: '30px',}}
          >
      <option value="English">English</option>
<option value="Spanish">Spanish</option>
<option value="French">French</option>
<option value="German">German</option>
<option value="Portuguese">Portuguese</option>
<option value="Italian">Italian</option>
<option value="Dutch">Dutch</option>
<option value="Russian">Russian</option>
<option value="Arabic">Arabic</option>
<option value="Polish">Polish</option>
<option value="Turkish">Turkish</option>
<option value="Swedish">Swedish</option>
<option value="Norwegian">Norwegian</option>
<option value="Danish">Danish</option>
<option value="Greek">Greek</option>
          </select>
          <p className="settingHelp">
            Choose the language in which the AI should generate emails. The AI will try to answer in this language.
          </p>
        </div>
        
        <div className="settingField">
          <label>Temperature: {aiSettings.temperature}</label>
          <input 
            style={{height: '5px', background: 'linear-gradient(90deg,rgb(193, 217, 249) 0%,rgb(213, 228, 244) 100%)', border:'none' ,  outline: 'none', boxShadow: 'none'}}
            type="range" 
            name="temperature" 
            min="0" 
            max="1" 
            step="0.1" 
            value={aiSettings.temperature} 
            onChange={handleAiChange}
            className="temperatureSlider"
          />
          <p className="settingHelp">
            Adjust the randomness of the AI's responses. Lower values are more focused and deterministic, while higher values are more creative and random.
          </p>
        </div>
        
        <div className="settingField">
          <label>Reply Agent Temperature: {aiSettings.replyAgentTemperature}</label>
          <input 
            style={{height: '5px', background: 'linear-gradient(90deg,rgb(193, 217, 249) 0%,rgb(213, 228, 244) 100%)', border:'none' ,  outline: 'none', boxShadow: 'none'}}
            type="range" 
            name="replyAgentTemperature" 
            min="0" 
            max="1" 
            step="0.1" 
            value={aiSettings.replyAgentTemperature} 
            onChange={handleAiChange}
            className="temperatureSlider"
          />
          <p className="settingHelp">
            Controls the creativity level specifically for reply generation. Lower values create more consistent, professional responses, while higher values allow for more varied and creative replies.
          </p>
        </div>
        
        <div className="settingField">
          <label>Max. Tokens:</label>
          <input 
            type="number" 
            name="max_tokens" 
            min="100" 
            max="1000" 
            value={aiSettings.max_tokens} 
            onChange={handleAiChange}
            className="tokenInput"
          />
          <p className="settingHelp">
            Limits the maximum length of the generated email.
          </p>
        </div>

        {/* <div className="settingActions">
          <button 
            onClick={saveAiSettings} 
            className="saveButton"
            disabled={loading}
          >
            {loading ? "Speichern..." : "Einstellungen speichern"}
          </button>
          {message && <span className="successMessage">{message}</span>}
        </div> */}
      </div>
      
      {/* Auto-Draft Creation Toggle - am Ende der AI Settings */}
      <div className="settingsGroup1" style={{ marginTop: '30px' }}>
        <div className="autoProcess_activation">
          {/* Smart Inbox Toggle */}
          <div className="autoProcess_toggle_container" style={{ marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '20px' }}>
            <div className="autoProcess_toggle_header">
              <h4 className="autoProcess_toggle_title">📥 Smart Inbox</h4>
              <div 
                className={`autoProcess_toggle ${inboxActive ? 'active' : ''}`} 
                onClick={handleToggleInboxActive}
              >
                <div className="autoProcess_toggle_switch"></div>
              </div>
            </div>
            <p className="autoProcess_toggle_description">
              {inboxActive 
                ? "Smart Inbox is active - emails are being retrieved automatically" 
                : "Enable automatic email retrieval and storage in your Smart Inbox"}
            </p>
            {inboxActive && (
              <p className="autoProcess_privacy_note" style={{ color: '#10b981', fontSize: '12px' }}>
                ✅ Active: Your email data is securely stored on BonusClub's servers to power smart features
              </p>
            )}
            {!inboxActive && (
              <p className="autoProcess_privacy_note" style={{ color: '#6b7280', fontSize: '12px' }}>
                By activating Smart Inbox, you agree that your email data will be securely stored on BonusClub's servers to enable our smart features and improve your experience.
              </p>
            )}
          </div>
          
          {/* Auto-Draft Creation Toggle */}
          <div className="autoProcess_toggle_container">
            <div className="autoProcess_toggle_header">
              <h4 className="autoProcess_toggle_title">🤖 Auto-Draft Creation</h4>
              <div 
                className={`autoProcess_toggle ${aiSettings.autoProcessEmails ? 'active' : ''}`} 
                onClick={() => setAiSettings(prev => ({
                  ...prev,
                  autoProcessEmails: !prev.autoProcessEmails
                }))}
              >
                <div className="autoProcess_toggle_switch"></div>
              </div>
            </div>
            <p className="autoProcess_toggle_description">
              {aiSettings.autoProcessEmails 
                ? "Agents automatically create drafts for new emails based on enthusiasm level" 
                : "Enable automatic draft creation by AI agents for incoming emails"}
            </p>
            {aiSettings.autoProcessEmails && (
              <p className="autoProcess_privacy_note" style={{ color: '#10b981', fontSize: '12px' }}>
                ✅ Active: New emails will be automatically processed by your agents
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Funktion zum Löschen eines Imports (Firestore)
  const handleDeleteImport = async () => {
    if (!selectedImport || deleteConfirmText !== selectedImport) {
      setMessage("Bitte bestätige den Import-Namen korrekt, um fortzufahren.");
      return;
    }

    try {
      setIsDeleting(true);
      setMessage(`Lösche Import "${selectedImport}" - bitte warten...`);

      const batch = writeBatch(firestore);

      // Einträge aus der 'db' Sammlung zum Löschen vorbereiten
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const q = query(dbCollectionRef, where("import_name", "==", selectedImport));
      const querySnapshot = await getDocs(q);
      const deletedCount = querySnapshot.size;

      if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
      }

      // Dokument aus der 'import_names' Sammlung zum Löschen vorbereiten
      const importNameDocRef = doc(firestore, `World/${userId}/import_names`, selectedImport);
      batch.delete(importNameDocRef);

      // Batch-Löschung ausführen
      await batch.commit();

      setMessage(`Import "${selectedImport}" erfolgreich gelöscht. ${deletedCount} Einträge wurden entfernt.`);

      // Lokalen State aktualisieren
      setImports(imports.filter(imp => imp !== selectedImport));
      setSelectedImport('');
      setDeleteConfirmText('');
      setImportCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[selectedImport];
        return newCounts;
      });

    } catch (error) {
      console.error("Fehler beim Löschen des Imports aus Firestore:", error);
      setMessage(`Fehler: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Neue Funktion für den WhatsApp Tab
  const renderWhatsappTab = () => (
    <WhatsappSettings userId={userId} />
  );

  // Neue Funktion für den Whisper Tab
  const renderWhisperTab = () => (
    <WhisperSettings userId={userId} />
  );

  // Neue Funktion für den Danger Zone Tab
  const renderDangerZoneTab = () => (
    <div className="settingsGroup dangerZone">
      <h3>Danger Zone</h3>
      <div className="dangerZoneWarning">
        <span className="warningIcon">⚠️</span>
        <p>
          Warning: Actions in this area cannot be undone.
          Please be careful as deleted data cannot be recovered.
        </p>
      </div>
      
      <div className="dangerZoneSection">
        <h4>Delete Import Data</h4>
        <p>
          Here you can remove all entries of a specific import from the database.
          This affects all records marked with the selected import ID.
        </p>
        
        {imports.length > 0 ? (
          <div className="deleteImportContainer">
            <div className="settingField">
              <label>Select import:</label>
              <select
                value={selectedImport}
                onChange={(e) => {
                  setSelectedImport(e.target.value);
                  setDeleteConfirmText('');
                }}
                className="importSelect"
              >
                <option value="">-- Please select --</option>
                {imports.map(imp => (
                  <option key={imp} value={imp}>
                    {imp} ({importCounts[imp] || 0} entries)
                  </option>
                ))}
              </select>
            </div>
            
            {selectedImport && (
              <div className="deleteConfirmation">
                <div className="settingField">
                  <label>Enter "{selectedImport}" to confirm:</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={`Enter "${selectedImport}"`}
                    className="deleteConfirmInput"
                  />
                </div>
                
                <button
                  className="deleteImportButton"
                  onClick={handleDeleteImport}
                  disabled={isDeleting || deleteConfirmText !== selectedImport}
                >
                  {isDeleting ? "Deleting..." : `Delete import (${importCounts[selectedImport] || 0} entries)`}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="noImportsMessage">No imports found.</p>
        )}
      </div>
    </div>
  );

  // Funktion zum Speichern der AI-Einstellungen
  const saveAiSettings = async () => {
    setMessage("");
    setLoading(true);
    
    try {
      await update(ref(database, `/World/${userId}/settings/ai`), aiSettings);
      setMessage("AI settings successfully saved!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving AI settings:", error);
      setMessage("Error saving AI settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Funktion zum Zurücksetzen der Prompt-Vorlage auf den Standardwert
  const resetToDefaultTemplate = () => {
    setAiSettings(prev => ({
      ...prev,
      promptTemplate: `You are \${actualSenderName}, writing casual but professional emails to potential business partners. 
You work at BonusClub, a company helping hosts boost their online visibility through authentic guest experiences and reviews.

Write in a natural, conversational style as if you're sending a quick email to a colleague. 
Keep it human, short, friendly, and to the point - like a real person would write.

CRITICAL RULES (MUST FOLLOW):
- Write in first person AS \${actualSenderName} (use "I" and write from \${actualSenderName}'s perspective)
- Address the recipient by their first name (\${firstName || 'Dear'})
- NEVER include any kind of signature, closing phrases, other than "Best, \${actualSenderName}".
- Just end with your last signature - no goodbyes, no sign-offs
- Keep it brief and genuine
- DO NOT include any footer, signature, or closing line
- IMPORTANT: Write the entire email in \${language || 'English'} language`
    }));
  };

  const resetStrictInstructions = () => {
    setAiSettings(prev => ({
      ...prev,
      strictInstructions: `- Start with a proper introduction that builds rapport (especially for first contact)
- If this is the first email ("Introduce Yourself"), make sure to properly introduce yourself and your company
- Include a brief personal touch or connection to build rapport (e.g., compliment their business, mention something specific you noticed)
- Address them personally (use their first name if available)
- Write like a real person sending a quick email
- Keep it short and sweet (max 4-5 sentences)
- NO greetings or signatures at the end
- End naturally with your last content point - DO NOT add any closing phrase
- Avoid any marketing-speak or formal language
- NEVER add your name, "thanks", "cheers", "regards" or ANY closing phrase at the end
- The email should end abruptly with your last content sentence`
    }));
  };

  // Funktion zum Zurücksetzen der Cleanup-Richtlinien auf den Standardwert
  const resetCleanupGuidelines = () => {
    setAiSettings(prev => ({
      ...prev,
      cleanupGuidelines: `- Make sure the contact is a female entrepreneur or personal brand and remove all other entries that does not fit our requirements.
- Verify the contact has a business related to hospitality, wellness, or lifestyle.
- Check if the contact has sufficient online presence (website, social media).
- Ensure the contact is a decision-maker (founder, owner, CEO, director).
- Remove contacts from large corporate chains or franchises.
- Keep only contacts with businesses that align with our premium positioning.
- Filter out contacts with incomplete or outdated information.
- Prioritize contacts with potential for authentic review generation.`
    }));
  };

  // Funktion zum Zurücksetzen der Reply Agent Guidelines auf den Standardwert
  const resetReplyAgentGuidelines = () => {
    setAiSettings(prev => ({
      ...prev,
      replyAgentGuidelines: `- Analyze the incoming email's tone and enthusiasm level
- Match the sender's communication style and formality level
- Reference specific points mentioned in the original email
- Keep responses concise and focused on the main topic
- Show genuine interest in their business or inquiry
- Provide helpful information without being overly promotional
- Use natural language that sounds conversational
- Address any questions or concerns directly
- Maintain professional boundaries while being friendly
- End with a clear next step or invitation for further discussion`
    }));
  };

  // Neue Funktion für den Agents Tab
  const renderAgentsTab = () => (
    <AgentsSettings userId={userId} />
  );

  // Funktion zum Ändern des Smart Inbox Status
  const handleToggleInboxActive = async () => {
    if (!userId) return;

    const newStatus = !inboxActive;
    setInboxActive(newStatus);
    
    try {
      // Speichere den neuen Status in der Datenbank
      const inboxActiveRef = ref(database, `/World/${userId}/settings/inboxActive`);
      await set(inboxActiveRef, newStatus);
      console.log(`Smart Inbox status set to ${newStatus ? 'active' : 'inactive'}`);
    } catch (error) {
      console.error('Error saving inboxActive status:', error);
      setInboxActive(!newStatus); // Zurücksetzen bei Fehler
    }
  };

  if (!userId) {
    return <p className="errorMessage">Please login to save settings.</p>;
  }

  return (
    <div className="worldSettingsContainer">
      {loading ? (
        <p className="loadingMessage">Loading settings...</p>
      ) : (
        <div className="settingsForm">
          <div className="settingsTabs">
            <button 
              className={`settingsTab ${activeTab === 'ai' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              AI Settings
            </button>
            <button 
              className={`settingsTab ${activeTab === 'email' ? 'active' : ''}`}
              onClick={() => setActiveTab('email')}
            >
              Email Accounts
            </button>
            <button 
              className={`settingsTab ${activeTab === 'emailPreferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('emailPreferences')}
            >
              Email Footer
            </button>
            <button 
              className={`settingsTab ${activeTab === 'topics' ? 'active' : ''}`}
              onClick={() => setActiveTab('topics')}
            >
              Topics
            </button>
            <button 
              className={`settingsTab ${activeTab === 'agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('agents')}
            >
              Agents
            </button>
            <button 
              className={`settingsTab ${activeTab === 'aiTags' ? 'active' : ''}`}
              onClick={() => setActiveTab('aiTags')}
            >
              AI Tags
            </button>
            <button 
              className={`settingsTab ${activeTab === 'extraSettings' ? 'active' : ''}`}
              onClick={() => setActiveTab('extraSettings')}
            >
              Extra Settings
            </button>
            <button 
              className={`settingsTab ${activeTab === 'whatsapp' ? 'active' : ''}`}
              onClick={() => setActiveTab('whatsapp')}
            >
              WhatsApp
            </button>
            <button 
              className={`settingsTab ${activeTab === 'whisper' ? 'active' : ''}`}
              onClick={() => setActiveTab('whisper')}
            >
              Whisper
            </button>
            <button 
              className={`settingsTab dangerTab ${activeTab === 'dangerZone' ? 'active' : ''}`}
              onClick={() => setActiveTab('dangerZone')}
            >
              Danger Zone
            </button>
          </div>

          {activeTab === 'ai' ? renderAiTab() :
          activeTab === 'email' ? renderEmailTab() : 
          activeTab === 'emailPreferences' ? renderEmailPreferencesTab() : 
          activeTab === 'topics' ? <WorldSettingsTopics userId={userId} /> :
          activeTab === 'whatsapp' ? renderWhatsappTab() :
          activeTab === 'whisper' ? renderWhisperTab() :
          activeTab === 'dangerZone' ? renderDangerZoneTab() :
          activeTab === 'agents' ? renderAgentsTab() :
          activeTab === 'aiTags' ? <AITagsSettings userId={userId} /> :
          activeTab === 'extraSettings' ? <MoreWorldSettings userId={userId} /> : null}

          {activeTab !== 'dangerZone' && activeTab !== 'whatsapp' && activeTab !== 'whisper' && activeTab !== 'email' && activeTab !== 'agents' && activeTab !== 'extraSettings' && (
            <div className="settingActions">
              <button 
                className={`saveSettingsButton ${isSaved ? 'saved' : ''}`} 
                onClick={handleSave}
                style={{
                  backgroundColor: isSaved ? '#10b981' : 'var(--blue)',
                  transition: 'background-color 0.3s ease'
                }}
              >
                {isSaved ? 'Saved!' : 'Save Settings'}
              </button>
            </div>
          )}

          {message && <p className="world_settings_message">{message}</p>}
        </div>
      )}
    </div>
  );
};

export default WorldSettings; 